import { useState } from 'react';
import { recordsApi, type CallSettingsView } from '../../api/client';
import { Card, LoadError, Loading, Notice } from '../../components/ui';
import { useData } from '../../lib/useData';

type Setting = 'voicemailEmails' | 'defaultSend' | 'defaultAttach' | 'recordingTranscripts' | 'meetingRecording';

/**
 * Company Settings: voicemail emails from Stitch, and recorded calls written out automatically.
 * Both off until the company's admins switch them on.
 */
export function CallRecordsSettingsCards({ domain }: { domain: string }) {
  const settings = useData(() => recordsApi.callSettings(domain), [domain], { live: false });
  const [busy, setBusy] = useState<Setting | null>(null);
  const [error, setError] = useState('');
  const [fresh, setFresh] = useState<CallSettingsView | null>(null);
  const s = fresh ?? settings.data;

  async function save(key: Setting, value: boolean) {
    setBusy(key);
    setError('');
    try {
      setFresh(await recordsApi.saveCallSettings(domain, { [key]: value }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save.');
    } finally {
      setBusy(null);
    }
  }

  if (settings.error) {
    return (
      <Card title="Voicemail emails">
        <LoadError error={settings.error} retry={settings.reload} />
      </Card>
    );
  }
  if (!s) {
    return (
      <Card title="Voicemail emails">
        <Loading label="Loading voicemail emails" />
      </Card>
    );
  }
  if (!s.editable) return null;

  const check = (key: Setting, label: string, hint: string, disabled = false) => (
    <label className="check-row">
      <input type="checkbox" checked={s[key]} disabled={disabled || busy !== null} onChange={(e) => void save(key, e.target.checked)} />
      <span className="cell-stack">
        <span>{label}</span>
        <small className="muted">{hint}</small>
      </span>
    </label>
  );

  return (
    <>
      <Card title="Voicemail emails" id="voicemail-emails">
        <div className="card-body settings-section">
          <p className="muted settings-lede">
            Stitch emails each new voicemail to the email address on its extension in the phone system (not to the people who sign in as it): who called, when, how
            long, the transcript (when voicemail transcripts are on for that mailbox) and the audio.
          </p>
          {check('voicemailEmails', 'Email new voicemail', s.voicemailEmails ? 'On: each new voicemail is emailed once it’s in the mailbox.' : 'Off: nothing is emailed by Stitch.')}
          {s.voicemailEmails && (
            <fieldset className="plain-fieldset records-defaults">
              <legend className="field-label">For each mailbox, unless someone signed in as it chooses under Your settings</legend>
              {check('defaultSend', 'Email each mailbox’s voicemail', s.defaultSend ? 'Every mailbox’s voicemail is emailed; each can be turned off.' : 'Nothing is emailed unless it’s turned on for a mailbox.')}
              {check('defaultAttach', 'Attach the audio', 'Up to about 8 MB. Longer messages aren’t attached: their email says to listen in Stitch. Shared boxes follow this too.')}
            </fieldset>
          )}
          <Notice tone="warn" title="Avoid two emails for every voicemail">
            When this is on, the phone system’s own voicemail emails should be off for your mailboxes (Voicemail Notifications: No Email Notifications). Stitch support can
            switch them off for you.
          </Notice>
          {s.voicemailEmails && !s.service && <p className="muted small-print">Emails start once Stitch support has connected your phone system’s call records to Stitch.</p>}
          {error && <Notice tone="crit">{error}</Notice>}
        </div>
      </Card>
      <Card title="Call recording transcripts" id="recording-transcripts">
        <div className="card-body settings-section">
          <p className="muted settings-lede">
            Stitch can write out every recorded call, so you can read it in the call’s full details, turn by turn: the caller, and the person who took the call. Anyone who
            can play a recording can also ask for that one to be written out (Transcribe), whatever this is set to.
          </p>
          {check(
            'recordingTranscripts',
            'Write out recorded calls automatically',
            !s.transcription ? 'Not available yet: Stitch’s transcription service isn’t set up.' : s.recordingTranscripts ? 'On: each recorded call is written out after it ends.' : 'Off: only the ones someone asks for.',
            !s.transcription && !s.recordingTranscripts
          )}
        </div>
      </Card>
      <Card title="Meeting recording" id="meeting-recording">
        <div className="card-body settings-section">
          <p className="muted settings-lede">
            Let the person who set up a meeting (and your admins) record it, with <strong>Record</strong> in the meeting. Everyone in it is told it’s being recorded, and
            so is anyone who joins while it is. Recordings are on the meeting’s page, with their transcript when recorded calls are written out automatically (above).
          </p>
          {check(
            'meetingRecording',
            'Hosts can record meetings',
            s.meetingRecording ? 'On: Record shows in meetings for their hosts and your admins.' : 'Off: meetings aren’t recorded.'
          )}
        </div>
      </Card>
    </>
  );
}
